import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/*
 * Drawer — a panel that opens over the screen from the right.
 *
 * For work that belongs to the page behind it: opening a step should not lose
 * the person's place in the list they opened it from. Anything that is its own
 * destination gets a route instead, not this.
 *
 * The whole panel scrolls as one, head, body and footer together; nothing is
 * pinned, so a long drawer reads like a page and never hides its own title.
 *
 * Two widths. `panel` is the reading width for a step, a review, a run. `wide`
 * is for a whole platform surface completing in here rather than on a page of
 * its own: those are built to a 896px measure, and squeezing one into 560
 * would be a reason to send people away again.
 *
 * A surface mounted in here brings its own heading, so `head="compact"` drops
 * this one to a line of context. Two page titles stacked is not a smaller
 * problem than none.
 */

export type DrawerSize = 'panel' | 'wide';

const WIDTH: Record<DrawerSize, string> = {
  panel: 'max-w-[560px]',
  wide: 'max-w-[960px]',
};

export interface DrawerProps {
  onClose: () => void;
  size?: DrawerSize;
  head?: 'full' | 'compact';
  /** Sits above the title: where this came from. */
  kicker?: React.ReactNode;
  /** Top-right of the kicker row, beside the close control. */
  badge?: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
  /** Pinned below the scrolling body. */
  footer?: React.ReactNode;
  /** `modal` when the drawer opens from inside a full-screen modal, so it lands on top of it. */
  layer?: 'page' | 'modal';
  children: React.ReactNode;
}

const LAYER: Record<'page' | 'modal', string> = {
  page: 'z-[190]',
  modal: 'z-[210]',
};

export const Drawer: React.FC<DrawerProps> = ({
  onClose, size = 'panel', head = 'full', kicker, badge, title, sub, footer, layer = 'page', children,
}) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className={`fixed inset-0 ${LAYER[layer]} flex justify-end bg-ink/25 backdrop-blur-sm`}
      role="presentation"
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={`flex h-full w-full flex-col overflow-y-auto bg-white shadow-[-16px_0_48px_rgba(24,25,29,0.16)] ${WIDTH[size]}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 pt-5 sm:px-[30px] sm:pt-[26px]">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              {kicker}
              {badge}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-line-strong bg-white text-ink transition-colors hover:bg-canvas-inset"
            >
              <X size={16} />
            </button>
          </div>
          {head === 'full' && (
            <>
              <h2 className="m-0 mt-5 text-[26px] font-bold leading-[33.2px] tracking-[-0.015em] text-ink sm:text-[32px] sm:leading-[40.4px]">
                {title}
              </h2>
              {sub && <p className="m-0 mt-2.5 text-[14px] leading-[23px] text-ink-muted">{sub}</p>}
            </>
          )}
          {head === 'compact' && (
            <p className="m-0 mt-2.5 text-[13px] leading-[21.5px] text-ink-muted">{title}</p>
          )}
        </div>

        <div className="flex-1 px-5 py-5 sm:px-[30px]">{children}</div>

        {footer && (
          <div className="shrink-0 border-t border-line bg-white px-5 py-4 sm:px-[30px]">{footer}</div>
        )}
      </div>
    </div>
  );
};
